import { P2GBox, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { ContentBlock } from '~/components/core/content-block/content-block';
import { DrawerNavigationType } from '~/lib/types/enums';
import { DrawerNavigationMenu } from './quote-card-info-header';
import { lazy, Suspense, useMemo } from 'react';
import { doesQuoteRequirePrinter, isQuoteDropOff, isQuoteLockerDropOff, quoteProtectionOptions } from '~/lib/quotes';
import { BasketQuote } from '~/lib/types/basket-types';
import { DefaultServiceBenefits } from '~/components/core/sidebar/default-service-benefits/default-service-benefits';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { WeightAndSize } from './weight-and-size';
import { Label } from '~/lib/types/extra-types';
import { ParcelContentsProtected } from './parcel-protection-tab';
import { PostCodeSearchInput } from './postcode-input';
import { useLocalisedLink } from '@parcel2go/npm-p2g-app';
import { useQuoteContent } from '~/hooks/use-quote-content';
import { PublicSettings } from '~/lib/ui-serverless-settings';

interface Props {
  quote: BasketQuote;
  selectedNavigationType: DrawerNavigationType;
  onSelectNavigationType: (navigationType: DrawerNavigationType) => void;
}

const LazyCmsContent = lazy(() => import('~/components/core/content/cms-content'));

const QuoteCardAdditionalInfo = ({ quote, selectedNavigationType, onSelectNavigationType }: Props) => {
  useQuoteContent(quote.service.courierSlug, quote.service.slug);

  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const { configLink } = useLocalisedLink();

  const isDropoff = useMemo(() => isQuoteDropOff(quote), [quote]);
  const protection = useMemo(() => quoteProtectionOptions(quote), [quote]);
  const requiresPrinter = useMemo(() => doesQuoteRequirePrinter(quote), [quote]);
  const hasPrintInStore = useMemo(() => !!quote?.availableExtras?.some((x) => x.key === Label.STORE), [quote]);
  const prohibLink = useMemo(() => configLink(PublicSettings.LINKS.PROHIB_ITEMS), [configLink]);

  return (
    <>
      <P2GBox
        sx={{
          position: 'sticky',
          backgroundColor: 'background.paper',
          top: 0,
          py: 2,
          zIndex: '100'
        }}
      >
        <DrawerNavigationMenu
          selectedNavigationType={selectedNavigationType}
          onSelectNavigationType={onSelectNavigationType}
          isDropOff={isDropoff}
        />
      </P2GBox>
      <P2GGrid container direction="column" spacing={1}>
        {selectedNavigationType === DrawerNavigationType.Details ? (
          <>
            <P2GGrid item>
              <P2GText variant="h4">
                {t('DRAWER_SERVICE_DETAILS_SUBTITLE', { defaultValue: 'Service Details' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <Suspense>
                <LazyCmsContent contentKey="QUOTE-DRAWER-SERVICE-BENEFITS">
                  <DefaultServiceBenefits
                    currency={quote.currency}
                    includedCoverAmountMinorCurrency={Math.max(
                      protection.included,
                      quote.service.includedProtection || 0
                    )}
                    isDropOff={isDropoff}
                    isLocker={isQuoteLockerDropOff(quote)}
                    quoteDates={quote.dates}
                    requiresPrinter={requiresPrinter}
                    title={t('DEFAULT_SERVICE_BENEFITS_TITLE', {
                      defaultValue: 'About this {{courier}} Service',
                      courier: quote.service.courier
                    })}
                  />
                </LazyCmsContent>
              </Suspense>
            </P2GGrid>
            <P2GGrid item>
              <WeightAndSize restrictions={quote.restrictions} />
            </P2GGrid>
            <P2GGrid item>
              <Suspense>
                <LazyCmsContent contentKey="QUOTE-DRAWER-PRINT-IN-SHOP-CONTENT">
                  {hasPrintInStore && (
                    <ContentBlock title={t('PRINT_IN_SHOP_SUBTITLE', { defaultValue: 'Print-in-shop' })}>
                      <P2GBox dataTestId="print-in-shop-element">
                        {t('DRAWER_PRINT_IN_SHOP_DESCRIPTION', {
                          defaultValue:
                            'If you opted to have your label printed in store, make sure you take your parcel along with the QR code provided. You will need to check with the shop to see if this functionality is available.'
                        })}
                      </P2GBox>
                    </ContentBlock>
                  )}
                </LazyCmsContent>
              </Suspense>
            </P2GGrid>
            <P2GGrid item>
              <Suspense>
                <LazyCmsContent contentKey="QUOTE-DRAWER-PROHIBITED-ITEMS-CONTENT">
                  <ContentBlock title={t('DRAWER_PROHIBITED_ITEMS_SUBTITLE', { defaultValue: 'Prohibited items' })}>
                    <P2GBox dataTestId="prohibited-items-element">
                      <>
                        {t('DRAWER_PROHIBITED_ITEMS_DESCRIPTION', {
                          defaultValue:
                            'We have a list of prohibited items and a list of items we are able to protect for loss but not damage. '
                        })}
                        {prohibLink && (
                          <P2GLink data-testid="ProhibitedItemsLinkTestId" href={prohibLink} target="_blank">
                            {t('DRAWER_PROHIBITED_ITEMS_LINK', { defaultValue: 'View List' })}
                          </P2GLink>
                        )}
                      </>
                    </P2GBox>
                  </ContentBlock>
                </LazyCmsContent>
              </Suspense>
            </P2GGrid>
            <P2GGrid item>
              <Suspense>
                <LazyCmsContent contentKey="DRAWER_DETAILS_TAB_FOOTER">
                  <P2GText variant="caption" colour="textSecondary">
                    {t('DRAWER_DETAILS_TAB_FOOTER_TEXT', {
                      defaultValue: ' * Collection and deliveries are not guaranteed.'
                    })}
                  </P2GText>
                </LazyCmsContent>
              </Suspense>
            </P2GGrid>
          </>
        ) : null}

        {selectedNavigationType === DrawerNavigationType.DropOff ? (
          <>
            <P2GGrid item>
              <P2GText variant="h4" dataTestId="drop-off-tab">
                {t('FIND_NEAREST_DROP_OFF_TEXT', { defaultValue: 'Find Nearest Drop Off' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <PostCodeSearchInput providerGroup={quote.service.dropOffProviderGroup} />
            </P2GGrid>
          </>
        ) : null}

        {selectedNavigationType === DrawerNavigationType.Protection ? (
          <>
            <P2GGrid item>
              <P2GText variant="h4">
                {t('DRAWER_PARCEL_PROTECTION_SUBTITLE', { defaultValue: 'Parcel Protection' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <ParcelContentsProtected
                extendedBaseCoverAvailable={Math.max(protection.included, quote.service.includedProtection || 0)}
                maximumCover={quote.service.maxProtection || 0}
                currency={quote.currency}
              />
            </P2GGrid>
          </>
        ) : null}
      </P2GGrid>
    </>
  );
};

export default QuoteCardAdditionalInfo;
